<template>
  <div v-if="isPickerEligible" ref="root">
    <StepLayout>
      <!-- Big left illustration (desktop only) — mirrors the funnel's two-column
           Step layout (e.g. the trade-in step) so telco matches the other items. -->
      <template #left>
        <StepIllustration
          class="hidden md:block"
          position="center_top"
          src="/img/product/funnel/step-mobile-plan-square-us.jpg"
        />
      </template>

      <template #right>
        <div class="space-y-16">
          <h3 id="telco-plan-picker-title" class="heading-2">
            {{ i18n(translations.title) }}
          </h3>

          <!-- "Learn more" lavender info card — opens the comparison drawer -->
          <CardGuidance
            clickable
            :icon="IconSim"
            :label="i18n(translations.learnMore)"
            :modal-name="PRODUCT.TELCO_COMPARISON_DRAWER"
            :tracking="{
              zone: 'telco_picker',
              name: 'telco_comparison_drawer',
            }"
          />

          <div
            aria-labelledby="telco-plan-picker-title"
            class="space-y-8"
            role="radiogroup"
          >
            <!-- No plan option — simple label wrapping a hidden radio + LargePicker -->
            <label class="group relative block cursor-pointer">
              <input
                :aria-label="i18n(translations.noPlanLabel)"
                :checked="!selectedPartnerName"
                class="absolute inset-0 opacity-0"
                name="telco-plan"
                type="radio"
                @change="onSelectNoPlan"
              />
              <LargePicker
                :index="0"
                :label="i18n(translations.noPlanLabel)"
                prefix="circle"
                :selected="!selectedPartnerName"
              />
            </label>

            <!-- Partner plan options — one LargePicker row per offer -->
            <label
              v-for="(option, idx) in options"
              :key="option.partnerName"
              class="group relative block cursor-pointer"
            >
              <input
                :aria-label="getPlanAriaLabel(option)"
                :checked="selectedPartnerName === option.partnerName"
                class="absolute inset-0 opacity-0"
                name="telco-plan"
                type="radio"
                :value="option.partnerName"
                @change="onSelectPlan(option)"
              />

              <LargePicker
                :index="idx + 1"
                :label="i18n(TELCO_PARTNER_DISPLAY_NAMES[option.partnerName])"
                :price="getPlanPrice(option)"
                :selected="selectedPartnerName === option.partnerName"
                :sub-label="getPlanSubLabel(option)"
              >
                <template #icon>
                  <div class="flex items-center gap-8">
                    <PickerRound
                      :disabled="false"
                      :index="idx + 1"
                      :selected="selectedPartnerName === option.partnerName"
                    />
                    <RevIllustration
                      alt=""
                      :height="40"
                      :src="TELCO_PARTNER_LOGO_PATHS[option.partnerName] ?? ''"
                      :width="40"
                    />
                  </div>
                </template>
                <template #price-prefix>
                  <template
                    v-for="priceOld in [getPlanPriceOld(option)]"
                    :key="`${option.partnerName}-${priceOld}-price-old`"
                  >
                    <s v-if="priceOld" class="text-action-default-low-disabled">
                      {{ priceOld }}
                    </s>
                  </template>
                </template>
              </LargePicker>
            </label>
          </div>
        </div>
      </template>
    </StepLayout>

    <ClientOnly>
      <TelcoComparisonDrawer />
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import { defineAsyncComponent, onUnmounted, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevIllustration } from '@ds/components/Illustration'
import { IconSim } from '@ds/icons/IconSim'
import { useIntersectionObserver } from '@vueuse/core'

import CardGuidance from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/CardGuidance/CardGuidance.vue'
import LargePicker from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/LargePicker/LargePicker.vue'
import PickerRound from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/PickerRound/PickerRound.vue'
import StepIllustration from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/StepIllustration/StepIllustration.vue'
import StepLayout from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/StepLayout/StepLayout.vue'
import {
  TELCO_PARTNER_DISPLAY_NAMES,
  TELCO_PARTNER_LOGO_PATHS,
  TELCO_PLAN_COMPARISONS,
} from '~/scopes/product-core/pages/ProductDetailPage/us/composables/useTelcoPicker/telcoPlanComparison.config'
import type {
  PartnerNames,
  TelcoPickerOption,
} from '~/scopes/product-core/pages/ProductDetailPage/us/composables/useTelcoPicker/types'
import { PRODUCT } from '~/scopes/product-core/route-names'

import translations from './TelcoPlanPicker.translations'

const TelcoComparisonDrawer = defineAsyncComponent(
  () => import('../TelcoComparisonDrawer/TelcoComparisonDrawer.vue'),
)

defineProps<{
  options: TelcoPickerOption[]
  selectedPartnerName: PartnerNames | null | undefined
  isPickerEligible: boolean
}>()

const emit = defineEmits<{
  'select-plan': [partnerName: PartnerNames | null]
}>()

const i18n = useI18n()
const { trackClick, trackProductBlockImpression } = useTracking()
const root = ref<HTMLElement | null>(null)

function getPlanConfig(option: TelcoPickerOption) {
  return TELCO_PLAN_COMPARISONS.find(
    (plan) => plan.partnerName === option.partnerName,
  )
}

function getPlanPrice(option: TelcoPickerOption): string {
  const config = getPlanConfig(option)

  return config?.priceNew ? i18n(config.priceNew) : ''
}

function getPlanPriceOld(option: TelcoPickerOption): string {
  const config = getPlanConfig(option)

  return config?.priceOld ? i18n(config.priceOld) : ''
}

function getPlanSubLabel(option: TelcoPickerOption): string {
  const config = getPlanConfig(option)

  return config?.pickerSubLine ? i18n(config.pickerSubLine) : ''
}

function getPlanAriaLabel(option: TelcoPickerOption): string {
  const name = i18n(TELCO_PARTNER_DISPLAY_NAMES[option.partnerName])
  const price = getPlanPrice(option)

  return price ? `${name} ${price}` : name
}

function onSelectNoPlan() {
  trackClick({ zone: 'telco_picker', name: 'no plan' })
  emit('select-plan', null)
}

function onSelectPlan(option: TelcoPickerOption) {
  trackClick({
    zone: 'telco_picker',
    // Lowercased partner enum as the tracking name — works for ANY partner
    // without an exhaustive map (which would break on union widening).
    name: option.partnerName.toLowerCase(),
  })
  emit('select-plan', option.partnerName)
}

const { stop } = useIntersectionObserver(
  root,
  ([{ isIntersecting }]) => {
    if (isIntersecting) {
      trackProductBlockImpression({
        block_name: 'telco_picker',
        products: [],
        product_price_tag_displayed: 'no_tag',
        product_price_tag_selected: false,
        product_model: '',
        product_category: '',
      })
      stop()
    }
  },
  { threshold: 0.5 },
)

onUnmounted(() => {
  stop()
})
</script>
